import { colors, createTheme, Fade, Slide } from "@mui/material"
import { forwardRef } from "react"
import defaultTheme from './default'
import { ExpandMore } from '@mui/icons-material'
import defaultDarkTheme from './defaultDarkTheme'

const SlideUpTransition = forwardRef( function SlideUpTransition ( props, ref ) {
    return <Slide direction="up" ref={ref} {...props} />
} )

function getFullscreenElement () {
    if ( typeof document === "undefined" ) return null
    // Standard + vendor-prefixed fallbacks
    return (
        document.fullscreenElement ??
        ( document ).webkitFullscreenElement ??
        ( document ).mozFullScreenElement ??
        ( document ).msFullscreenElement ??
        null
    )
}

const darkTheme = createTheme( {

    theme: "dark",
    border: [
        "none",
        '1px solid #444746',
    ],
    table: {
        headerBg: "#404040",
        headerColor: defaultDarkTheme.palette.text.secondary,
        color: '#FFFFFF',
        fontSize: "14px"
    },
    zIndex: {
        modal: 3000,
        tooltip: 5000,
    },
    layout: {
        filterElementHeight: "37.4px",
        pagePadding: { md: 2, xs: 1 },
        borderRadius: 4,
        cardsGap: 1,
        sectionsGap: 2,
    },
    palette: {
        mode: 'dark',
        borderColor: "#3E3E3E",
        inputBorder: "1px solid rgba(255, 255, 255, 0.23)",
        contentBg: "#171717",
        contentBgDark: "#202020",
        labelColor: '#fafafa',
        shadedText: "#eee",
        defaultColor: "#fff",
        greyedOut: "#3e3e3e",
        layoutBackground: "#1E1F20" || "#1E1E1E",
        cardBackground: "#131314" || "#171717",
        rowHover: "hsl(240 3.7% 15.9%)",

        text: {
            primary: "#e3e3e3",
            secondary: "#c4c7c5"
        },

        form: {
            labelColor: '#fafafa',
            formBg: "#121212",
            formCardBg: "#000",
            placeholder: "#aaa",
            inputBg: "#121215"
        },

        nav: {
            font: "#ffffff",
            sidebar: "#1E1F20",
            sidebarHover: "#FFFFFF1F",
            activeFont: "#000000" || "#ffffff",
            activeSidebarItem: "#90CAF9" || "#353535",
            sidebarItemHoverBg: "#90CAF911",
            selected: "#151D48"
        },
        common: {
            black: "#000000",
            white: "#ffffff",
            font: "#ffffff",
            bg: "#171717"
        },

        // Main colors
        // primary: {
        //     main: '#1d4ed8',
        //     light: '#3f6be4',
        //     dark: '#0d37aa',
        //     contrastText: '#fff'
        // },
        primaryDark: {
            main: '#eee',
            light: '#eee',
            dark: '#eee',
            contrastText: '#000'
        },
        errorMessage: {
            main: "#F44336",
            light: "#FF8D9D",
            dark: "#de5f71",
            contrastText: "#fff"
        },
        customThemeColor: {
            main: "#E25A26",
            light: "#d4653b",
            dark: "#d94c16",
            contrastText: "#fff"
        },
        warning: {
            main: "#ffc107",
            dark: "#b28704",
            light: "#ffcd38",
            contrastText: "#000000"
        },
        greyed: {
            main: colors.grey[400],
            light: colors.grey[200],
            dark: colors.grey[600],
            contrastText: "#fff"
        },
        contrast: {
            main: "#FFFFFF",
            light: "#FEFEFE",
            dark: "#FAFAFA",
            contrastText: "#000000"
        },
    },
    typography: {
        fontFamily: "'Poppins', sans-serif",
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,
        color: "#E3E3E3",
        h6: { color: "#E3E3E3" },
        h5: { color: "#E3E3E3" },
        h4: { color: "#E3E3E3" },
        h3: { color: "#E3E3E3" },
        h2: { color: "#E3E3E3" },
        h1: { color: "#E3E3E3", fontSize: "1rem", fontWeight: 600 },
        subtitle2: { color: "#c4c7c5", fontSize: "0.875rem", fontWeight: 500 },
        subtitle1: { color: "#E3E3E3", fontSize: "0.875rem", fontWeight: 400 },
        body1: { color: "#E3E3E3", fontSize: "0.875rem" },
        body2: { color: "#E3E3E3", fontSize: "0.75rem" },
    },
    components: {
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                    h1: 'h1',
                    h2: 'h1',
                    h3: 'h1',
                    h4: 'h1',
                    h5: 'h1',
                    h6: 'h1',
                    subtitle1: 'h6',
                    subtitle2: 'span',
                    body1: 'p',
                    body2: 'p',
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: "10px",
                    backgroundImage: "none",
                    // background: "#171717"
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: "20px",
                    border: "1px solid #27272a",
                    background: "#171717"
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    boxShadow: `0 10px 30px -10px rgba(0, 0, 0, 0.5), 0 4px 10px -5px rgba(0, 0, 0, 0.3)`,
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    background: "#121215",
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    //     borderColor: defaultDarkTheme.palette.primary.main,
                    //     borderWidth: "1px",
                    // },
                    // "&.Mui-focused": {
                    //     boxShadow: `0 0 0 1px ${ defaultDarkTheme.alpha( defaultDarkTheme.palette.primary.main, 0.4 ) }`,
                    // },
                },
                input: {
                    fontSize: "14px",
                    fontWeight: "300"
                }
            },
        },
        // MuiTextField: {
        //     styleOverrides: {
        //         root: {
        //             '& .MuiOutlinedInput-root': {
        //                 borderRadius: "20px",
        //             },
        //         }
        //     }
        // },
        MuiSelect: {
            styleOverrides: {
                root: {
                    "&.Mui-focused": {
                        boxShadow: "none", // glow effect outside
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: defaultDarkTheme.palette.divider, // Much darker on focus
                        borderWidth: '1px',   // Optional: make it slightly thicker
                    },
                }
            },
            defaultProps: {
                IconComponent: ExpandMore,
                MenuProps: {
                    anchorOrigin: {
                        vertical: "bottom", horizontal: "left"
                    },
                    transformOrigin: {
                        vertical: "top", horizontal: "left"
                    },
                    slotProps: {
                        paper: {
                            sx: { marginTop: "5px", bgcolor: "#1E1E1E", maxHeight: 300 }
                        }
                    }
                }
            }
        },

        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    margin: 0,
                    marginTop: "3px",
                    color: "#F44336",
                }
            }
        },

        MuiTableCell: {
            styleOverrides: {
                root: {
                    border: "none",
                    fontSize: "14px"
                }
            }
        },
        MuiMenu: {
            defaultProps: {
                autoFocus: true,
                slotProps: {
                    paper: {
                        className: "slim-custom-scrollbar",
                        sx: {
                            bgcolor: "#171717"
                        }
                    }
                }
            },
            styleOverrides: {
                paper: {
                    padding: "0",
                    borderRadius: "20px",
                    maxHeight: "350px",
                    marginTop: "5px"
                }
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    margin: "5px",
                    minHeight: 0,
                    borderRadius: "20px",
                    fontSize: "14px",
                    ":hover": {
                        background: "#e5e5e522",
                        color: "white"
                    },
                    '&.Mui-selected:hover': {
                        background: "primary.light",
                        color: "#fff"
                    },
                }
            }
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: 0,
                }
            }
        },
        MuiDialog: {
            defaultProps: {
                transitionDuration: {
                    enter: 200,
                    exit: 200
                },
                slots: {
                    transition: SlideUpTransition,
                },
                slotProps: {
                    backdrop: {
                        slots: {
                            transition: Fade,
                        },
                        transitionDuration: {
                            enter: 200,
                            exit: 0
                        },
                        // sx: {
                        //     background: `rgba(64, 64, 64, 0.4)`,
                        // }
                    },
                    paper: {
                        className: "custom-scrollbar",
                        sx: {
                            borderRadius: "20px",
                            width: "90%"
                        }
                    }
                }
            },
            styleOverrides: {
                root: ( { theme, ownerState } ) => ( {
                    background: "rgba(0, 0, 0, 0.7)",
                    '& .MuiDialog-container': {
                        [theme.breakpoints.down( 'sm' )]: { alignItems: "flex-end" },
                    },
                    '& .MuiDialog-paper': {
                        margin: 0,
                        maxWidth: ownerState.maxWidth || 1024,
                        width: "90%",
                        [theme.breakpoints.down( 'sm' )]: { width: "100%", maxHeight: '90%' },
                        borderRadius: { xs: `${theme.layout.borderRadius * 4}px ${theme.layout.borderRadius * 4}px 0 0`, sm: theme.layout.borderRadius },
                    },
                } )
            },
            variants: [
                {
                    props: { variant: 'dock-to-bottom' },
                    style: ( { theme, ownerState } ) => ( {
                        '& .MuiDialog-container': {
                            [theme.breakpoints.down( 'sm' )]: { alignItems: "flex-end" },
                        },
                        '& .MuiDialog-paper': {
                            margin: 0,
                            maxWidth: ownerState.maxWidth || 1024,
                            width: "90%",
                            [theme.breakpoints.down( 'sm' )]: { width: "100%", maxHeight: '90%' },
                            borderRadius: { xs: `${theme.layout.borderRadius * 4}px ${theme.layout.borderRadius * 4}px 0 0`, sm: theme.layout.borderRadius },
                        },
                    } )
                }
            ]
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    background: "rgba(48, 48, 48, 0.53)",
                }
            }
        },
        MuiTooltip: {
            defaultProps: {
                disableInteractive: true,
                slotProps: {
                    popper: {
                        container: ( anchorEl, a, b, c ) => {
                            const fs = getFullscreenElement()
                            // Prefer an HTMLElement; otherwise fall back to body
                            return ( fs instanceof HTMLElement ? fs : document.body )
                        },
                    }
                }
            }
        },
        MuiButton: {
            defaultProps: {
                loadingPosition: "start",
                disableElevation: true,
                size: "small"
            },
            styleOverrides: {
                root: {
                    textTransform: "capitalize",
                    borderRadius: 8,
                },
                sizeSmall: { fontSize: "0.75rem", minHeight: 32 },
                sizeMedium: { minHeight: 40 },
                sizeLarge: { minHeight: 48 },
            },
            variants: [
                {
                    props: { variant: 'muted', size: "small" },
                    style: ( { theme, ownerState } ) => {

                        const buttonColorProp = ownerState.color && ownerState.color !== 'inherit'
                            ? ownerState.color
                            : 'primary'

                        const targetColorPalette = theme.palette[buttonColorProp]

                        return {
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            fontSize: "0.75rem",
                            padding: "5px 10px",

                            // 3. Set the base text color to the palette's main color
                            color: theme.palette.text.secondary,

                            transition: theme.transitions.create( ['background-color', 'color'], {
                                duration: theme.transitions.duration.short,
                            } ),

                            '&:hover': {
                                backgroundColor: theme.alpha( targetColorPalette?.main || '0, 0, 0', 0.1 ),
                                color: targetColorPalette?.main || theme.palette.text.primary,
                            },
                        }
                    }
                },
                {
                    props: { variant: 'muted' },
                    style: ( { theme, ownerState } ) => {

                        const buttonColorProp = ownerState.color && ownerState.color !== 'inherit'
                            ? ownerState.color
                            : 'primary'

                        const targetColorPalette = theme.palette[buttonColorProp]

                        return {
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            fontSize: "0.75rem",

                            // 3. Set the base text color to the palette's main color
                            color: theme.palette.text.secondary,

                            transition: theme.transitions.create( ['background-color', 'color'], {
                                duration: theme.transitions.duration.short,
                            } ),

                            '&:hover': {
                                backgroundColor: theme.alpha( targetColorPalette?.main || '0, 0, 0', 0.1 ),
                                color: targetColorPalette?.main || theme.palette.text.primary,
                            },
                        }
                    }
                }
            ]
        },
        MuiIconButton: {
            variants: [
                {
                    props: { variant: 'muted', size: "small" },
                    style: ( { theme, ownerState } ) => {

                        const buttonColorProp = ownerState.color && ownerState.color !== 'inherit'
                            ? ownerState.color
                            : 'primary'

                        const targetColorPalette = theme.palette[buttonColorProp]

                        return {
                            backgroundColor: 'transparent',
                            textTransform: 'none',

                            // 3. Set the base text color to the palette's main color
                            color: theme.palette.text.secondary,

                            transition: theme.transitions.create( ['background-color', 'color'], {
                                duration: theme.transitions.duration.short,
                            } ),

                            '&:hover': {
                                backgroundColor: theme.alpha( targetColorPalette?.main || '0, 0, 0', 0.1 ),
                                color: targetColorPalette?.main || theme.palette.text.primary,
                            },
                        }
                    }
                },
                {
                    props: { variant: 'muted' },
                    style: ( { theme, ownerState } ) => {

                        const buttonColorProp = ownerState.color && ownerState.color !== 'inherit'
                            ? ownerState.color
                            : 'primary'

                        const targetColorPalette = theme.palette[buttonColorProp]

                        return {
                            backgroundColor: 'transparent',
                            textTransform: 'none',

                            // 3. Set the base text color to the palette's main color
                            color: theme.palette.text.secondary,

                            transition: theme.transitions.create( ['background-color', 'color'], {
                                duration: theme.transitions.duration.short,
                            } ),

                            '&:hover': {
                                backgroundColor: theme.alpha( targetColorPalette?.main || '0, 0, 0', 0.1 ),
                                color: targetColorPalette?.main || theme.palette.text.primary,
                            },
                        }
                    }
                }
            ]
        },

        MuiSkeleton: {
            defaultProps: {
                animation: "wave"
            },
            styleOverrides: {
                root: {
                    transform: "scale(1)",
                    borderRadius: 10,
                }
            }
        },

        // CUSTOM COMPOENENTS FRO DATE AND TIME PICKERS

        MuiPickerOutlinedInput: {
            defaultProps: {
                slotProps: {
                    textField: {
                        size: "small",
                        slotProps: {
                            input: {
                                sx: {
                                    bgcolor: "#121215"
                                }
                            }
                        }
                    },
                    desktopPaper: {
                        sx: {
                            border: '1px solid #27272A',
                            marginY: "5px",
                            bgcolor: "#1E1E1E"
                        }
                    },
                    mobilePaper: {
                        sx: {
                            border: '1px solid #27272A',
                            marginY: "5px",
                            bgcolor: "#1E1E1E",
                            zIndex: defaultTheme.zIndex.tooltip * 4
                        }
                    },
                    popper: {
                        sx: {
                            margin: "10px",
                            zIndex: defaultTheme.zIndex.tooltip * 4
                        }
                    }
                }
            }
        },
    },

} )

export default darkTheme